"use client"

import { useState, useEffect } from "react"
import {
  Home,
  Search,
  BookOpen,
  MapPin,
  ArrowLeft,
  RefreshCw,
  Mail,
  Compass,
  Heart,
  Star,
  Coffee,
  Smile,
} from "lucide-react"

// Données pour les suggestions
const suggestions = [
  
  {
    id: 2,
    title: "Universités du Kenya",
    description: "Explorez les universités kenyanes",
    icon: "MapPin",
    link: "/mksp/guide/university",
    color: "bg-blue-100 text-blue-800",
  },
  {
    id: 3,
    title: "Contact",
    description: "Besoin d'aide ? Contactez-nous",
    icon: "Mail",
    link: "/mksp/contact",
    color: "bg-red-100 text-red-800",
  },
]

const funFacts = [
  "Le Kenya compte plus de 40 groupes ethniques différents",
  "Le mont Kenya est le deuxième plus haut sommet d'Afrique",
  "Le Kenya est le berceau de l'humanité selon les découvertes archéologiques",
  "La grande migration au Masai Mara est l'un des spectacles les plus impressionnants au monde",
  "Nairobi est la seule capitale au monde avec un parc national",
]

// Composants UI
const Badge = ({ children, variant = "default", className = "" }) => {
  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium"
  const variants = {
    default: "bg-green-100 text-green-800",
    secondary: "bg-gray-100 text-gray-800",
    outline: "border dark:border-gray-600 border-gray-900 text-gray-700 bg-white",
  }
  return <span className={`${baseClasses} ${variants[variant]} ${className}`}>{children}</span>
}

const Card = ({ children, className = "" }) => {
  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm rounded-lg ${className}`}
    >
      {children}
    </div>
  )
}

const CardHeader = ({ children }) => {
  return <div className="p-6 pb-4">{children}</div>
}

const CardContent = ({ children }) => {
  return <div className="px-6 pb-6">{children}</div>
}

const CardTitle = ({ children, className = "" }) => {
  return (
    <h3 className={`text-lg font-semibold leading-none tracking-tight dark:text-white ${className}`}>{children}</h3>
  )
}

const CardDescription = ({ children, className = "" }) => {
  return <p className={`text-sm text-gray-600 dark:text-gray-300 ${className}`}>{children}</p>
}

const Button = ({ children, variant = "default", className = "", onClick, ...props }) => {
  const baseClasses =
    "inline-flex items-center justify-center text-sm font-medium transition-colors rounded focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
  const variants = {
    default: "bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2",
    outline:
      "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 h-10 px-4 py-2 text-gray-900 dark:text-white",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 h-10 px-4 py-2",
    ghost: "hover:bg-gray-100 dark:hover:bg-gray-700 h-10 px-4 py-2 text-gray-900 dark:text-white",
  }
  return (
    <button className={`${baseClasses} ${variants[variant]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  )
}

// Fonction pour rendre les icônes
const renderIcon = (iconName, className = "w-5 h-5") => {
  const icons = {
    Home: <Home className={className} />,
    Search: <Search className={className} />,
    BookOpen: <BookOpen className={className} />,
    MapPin: <MapPin className={className} />,
    ArrowLeft: <ArrowLeft className={className} />,
    RefreshCw: <RefreshCw className={className} />,
    Mail: <Mail className={className} />,
    Compass: <Compass className={className} />,
    Heart: <Heart className={className} />,
    Star: <Star className={className} />,
    Coffee: <Coffee className={className} />,
    Smile: <Smile className={className} />,
  }
  return icons[iconName] || <Home className={className} />
}

function NotFound() {
  const [currentFact, setCurrentFact] = useState(0)
  const [isSpinning, setIsSpinning] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFact((prev) => (prev + 1) % funFacts.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  const handleRefresh = () => {
    setIsSpinning(true)
    setTimeout(() => {
      setIsSpinning(false)
      window.location.reload()
    }, 1000)
  }

  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      window.location.href = "/"
    }
  }

  return (
    <div className="min-h-screen mtCGfreeUniv bg-gradient-to-br from-green-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      
      {/* Contenu Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          {/* Illustration du bel homme perdu */}
          <div className="mb-8">
            
          </div>

          {/* Titre et message d'erreur */}
          <div className="mb-8">
            <h1 className="text-6xl font-bold text-gray-900 dark:text-white mb-4">404</h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">
              Il semble que cette page ait disparu dans la savane kenyane...
            </p>
            <p className="text-lg text-gray-500 dark:text-gray-400">
              Mais ne vous inquiétez pas, nous allons vous aider à retrouver votre chemin !
            </p>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button onClick={() => (window.location.href = "/")} className="text-lg px-6 py-3">
              {renderIcon("Home", "w-5 h-5 mr-2")}
              Retour à l'accueil
            </Button>
            {/* <Button onClick={goBack} variant="outline" className="text-lg px-6 py-3">
              {renderIcon("ArrowLeft", "w-5 h-5 mr-2")}
              Page précédente
            </Button>
            <Button onClick={handleRefresh} variant="secondary" className="text-lg px-6 py-3" disabled={isSpinning}>
              {renderIcon("RefreshCw", `w-5 h-5 mr-2 ${isSpinning ? "animate-spin" : ""}`)}
              Réessayer
            </Button> */}
          </div>

          {/* Fait amusant sur le Kenya */}
         
        </div>

        {/* Suggestions de pages */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-center mb-8 text-gray-800 dark:text-white">
            Peut-être cherchiez-vous...
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {suggestions.map((suggestion) => (
              <Card
                key={suggestion.id}
                className="hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                onClick={() => (window.location.href = suggestion.link)}
              >
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className={`p-3 rounded-full ${suggestion.color}`}>
                      {renderIcon(suggestion.icon, "w-6 h-6")}
                    </div>
                  </div>
                  <CardTitle className="text-xl">{suggestion.title}</CardTitle>
                  <CardDescription>{suggestion.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <a href={suggestion.link} variant="outline" className="w-full inline-flex border p-4 hover:bg-green-700 items-center justify-center text-sm font-medium transition-colors rounded focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none">
                    Visiter cette page
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

       
      </div>

      
    </div>
  )
}

export default NotFound
